import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { buildLoginUrl } from '../../utils/authReturnUrl';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PublicCfg = { enabled: false } | { enabled: true };

export default function ReferralFab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, auth } = useAuth();
  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';
  const hideForStaff = isAuthenticated && auth.type !== 'CUSTOMER';

  const [cfg, setCfg] = React.useState<PublicCfg | null>(null);

  const load = React.useCallback(() => {
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/referrals/public-config`,
            navigator.language,
          ),
        );
        if (!res.ok) {
          setCfg({ enabled: false });
          return;
        }
        const data = (await res.json()) as PublicCfg;
        setCfg(data?.enabled ? { enabled: true } : { enabled: false });
      } catch {
        setCfg({ enabled: false });
      }
    })();
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  if (hideForStaff || !cfg?.enabled) return null;

  const go = () => {
    if (isCustomer) {
      navigate('/account/referrals');
      return;
    }
    navigate(buildLoginUrl('/account/referrals'));
  };

  return (
    <button
      type="button"
      onClick={go}
      className="fixed bottom-24 right-5 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-violet-600 text-white shadow-xl transition hover:scale-105 hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-amber-300/50"
      aria-label={t('storefront.referral_fab_aria', {
        defaultValue: 'Open referrals — invite friends and earn rewards',
      })}
      title={t('storefront.referral_fab_title', { defaultValue: 'Referrals' })}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full border border-amber-200/50 opacity-60 motion-safe:animate-ping"
        style={{ animationDuration: '3.2s' }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full border border-violet-300/40 opacity-50 motion-safe:animate-ping"
        style={{ animationDuration: '3.2s', animationDelay: '1.4s' }}
      />
      <Gift className="h-6 w-6" strokeWidth={2.2} />
    </button>
  );
}
